import type { BibleBookmark } from '@/types/bookmark';
import { containsRecoveredVoiceText } from '@/lib/audio-recovery';
import { useUnconfirmedSave } from '@/hooks/useUnconfirmedSave';
import UnconfirmedSaveWords from '@/components/UnconfirmedSaveWords';

/**
 * D4, bookmark equivalent (2026-10-18): read-only notice for a bound audio row
 * whose destination is THIS journal entry and whose save the server has not
 * confirmed (a restored device finds the registry record bound with
 * `receipt_missing`). It shows that row's words with Copy and nothing else:
 * no Retry, no Remove, no merge, no rebind, no resave, no write. It renders in
 * read-only mode too, inside the entry's own row, so the words never appear on
 * another entry, on a bare anchor (compose) or on a prayer. The "already
 * appear in this journal entry" line is a display hint from text containment,
 * never deletion authority.
 *
 * `suppressed` is the sheet's own saving latch: while its save is in flight,
 * a row that save has just bound is not shown as unconfirmed (the hook's trace
 * guard hides this device's pending or installed operation as well).
 */
interface Props {
  bookmark: BibleBookmark;
  suppressed: boolean;
}

export default function BookmarkUnconfirmedSaveNotice({ bookmark, suppressed }: Props) {
  const candidate = useUnconfirmedSave({
    type: 'bookmark',
    id: bookmark.id,
    updatedAt: bookmark.updatedAt,
  });
  if (suppressed || !candidate) return null;
  const words = candidate.transcribedText?.trim() ?? '';
  const alreadyPresent = containsRecoveredVoiceText(bookmark.note, words);

  return (
    <UnconfirmedSaveWords
      words={words}
      destination="bookmark"
      alreadyPresent={alreadyPresent}
    />
  );
}
